import type { StepSummary, FeatureFlagKey } from '@sb/feathers-types';
import type { DeviceKind, EquipmentItem } from '@sb/integrations/types';
import type { StepConfigurationByKind } from '@sb/remote-control/step/stepConfigurationByKind';
import type { CodeBlock, EnvironmentVariable } from '@sb/remote-control/types';
import type {
  BaseRoutineStepConfiguration,
  RoutineStepConfiguration,
  Space,
  SpeedProfile,
  StepKind,
} from '@sb/routine-runner';

import type { DistanceUnitInfo } from '../misc';
import type { Robot } from '../robot';
import type { Routine } from '../routine';

import type { Configuration as TConfiguration } from './configuration';

export namespace Step {
  /**
   * Supported 'args' by the routine runner schema.
   */
  export type Arguments = StepConfigurationByKind['args'] & {};

  // export interface Configuration extends zod.infer<typeof Configuration> {}
  export interface Configuration extends TConfiguration {}

  /** Add custom fields to the step configuration. */
  export type ConvertedConfiguration = StepConfigurationByKind & {
    id: string;
    description?: string;
    name: string;
    routineID: string;
    stepKind: StepKind;
    substepsMinimized?: boolean;
  };

  /**
   * Contains all fields required for displaying a list of steps
   * in the frontend. It doesn't include robot-specific
   * configuration. This summary is used for storing steps in the `/routines`
   * collection. It's mainly used for the reorder (drag-and-drop) feature.
   */
  export type Summary = StepSummary;

  export type ParentStep = Pick<Summary, 'id' | 'stepKind'> | null;

  /**
   * While "Summary" is the interface used in the database,
   * "ConvertedSummary" contains the format required in the frontend.
   */
  export interface ConvertedSummary extends Omit<Summary, 'steps'> {
    /**
     * When copying a step, this is set to source the config for the new step
     */
    copiedFromStepID?: string;

    name: string;

    /**
     * Some steps require a specific stepKind for the parent step.
     * For example, 'LoopControl' should be inside a 'Loop' step.
     *
     * By keeping track of the parent step when converting a step
     * summary, we can easily validate a step:
     *
     * ```
     * if (step.stepKind === 'LoopControl' && step.parentStep.stepKind !== 'Loop') {
     *   throw new Error('Restart Loop is currently not defined inside a Loop Step');
     * }
     * ```
     */
    parentSteps: ParentStep[];

    /**
     * In some places of the frontend, we need the position of
     * a step in the entire tree. For example:
     *
     * 1. Loop
     *    2. Nested loop step
     *    3. Another nested loop step
     * 4. Another loop step
     *    5. Nested loop step
     *       6. Nested in one more level
     *    7. Another nested step
     */
    stepPosition: number;
    /**
     * In some places of the frontend, we need the position of
     * a step relative to its siblings. For example:
     *
     * 1. step                       (siblingPosition: 0)
     *    2. nested step             (siblingPosition: 0)
     *    3. another nested step     (siblingPosition: 1)
     * 4. loop                       (siblingPosition: 1)
     *    5. nested step             (siblingPosition: 0)
     */
    siblingPosition: number;
    steps: ConvertedSummary[];
  }

  /**
   * Common arguments available for all steps, regardless of their stepKind.
   */
  export interface CommonArguments {
    /**
     * A stepKind can have different kinds of arguments. A "Loop" step, for example,
     * can also have the same arguments as the ones from "If" steps.
     *
     * Using an 'argumentKind' field helps us to improve typing. For example, this works:
     * if (step.argumentKind === 'Loop') console.log(step.times)
     *
     * However, this would give us a build error:
     * if (step.argumentKind === 'Loop') console.log(step.condition)
     *
     * Because 'step.condition' doesn't exist in loops that don't use conditional rules.
     * The same rule applies to conditional steps (i.e. If steps or loop
     * steps using conditional rules):
     *
     * // this works
     * if (step.argumentKind === 'If') console.log(step.condition)
     *
     * // this doesn't work
     * if (step.argumentKind === 'If') console.log(step.times)
     *
     * If we didn't have this 'argumentKind' field, then TypeScript wouldn't
     * be able to correctly identify the arguments because
     * each step can have different index signatures.
     *
     * So, these use cases would give us a build error:
     * console.log(step.times)
     * console.log(step.condition)
     *
     * Because the 'condition' field only exists for conditional steps
     * (i.e. If steps and Loop steps using conditional rules) while
     * the 'times' field only exists in certain loops, for example.
     *
     * Note that this 'argumentKind' field exists in the remote control only
     * and we ARE storing it in the database. However, this field does NOT
     * exist in the routine-runner as the routine-runner has its own dynamic
     * way of generating a schema but we're using a static approach in the
     * remote-control.
     */
    argumentKind: StepKind;
  }

  export interface ActionRequired {
    kind:
      | 'invalidConfiguration'
      | 'missingConfiguration'
      | 'deprecatedConfigurationWarning'
      | 'missingEquipment';
    message: string;
    fieldId?: string;
    // Used when `kind='deprecatedConfigurationWarning'`. Helps to determine whether deprecation warning should be shown (e.g. based on FFs).
    deprecationKind?: 'javaScriptExpressions';
  }

  export type Validator = (args: {
    step: ConvertedSummary;
    stepConfiguration: ConvertedConfiguration | undefined;
    routine: Routine.ConvertedResponse;
    equipment: EquipmentItem[];
    globalSpace: Space.Item[];
    globalVariables: EnvironmentVariable[];
    ioOutputs: Robot.IOPort[];
    featureFlags?: Record<string, boolean>;
  }) => void;

  export type ToRoutineRunner = (args: {
    stepConfiguration: ConvertedConfiguration;
    stepData: BaseRoutineStepConfiguration;
    equipment: EquipmentItem[];
    baseSpeedProfile: SpeedProfile;
    codeBlocks?: Omit<CodeBlock, 'created' | 'updated'>[];
  }) => RoutineStepConfiguration;

  export type GetStepDescription = (args: {
    stepConfiguration: ConvertedConfiguration;
    routine?: Routine.ConvertedResponse;
    robot?: Robot.ConvertedResponse;
    includeStepName?: boolean;
    distanceUnitInfo?: DistanceUnitInfo;
    globalSpace?: Space.Item[];
    globalVariables?: EnvironmentVariable[];
    equipment?: EquipmentItem[];
  }) => string | null;

  export enum LibrarySection {
    Basic = 'Basic',
    Control = 'Control',
    InterfaceWithMachines = 'Interface with other devices',
    InterfaceWithOperators = 'Interface with operators',
    Vision = 'Vision',
    Hidden = 'Hidden', // Hidden steps are not shown in the step library
  }

  /**
   * Identifies a family of steps that chain together for drag operations.
   */
  export type BlockType = 'ifElse';

  /**
   * Role of a step within a chainable block.
   *
   * - 'start': Begins a new block (e.g., If)
   * - 'continuation': Extends the block (e.g., ElseIf)
   * - 'end': Terminates the block, nothing can follow (e.g., Else)
   */
  export type BlockRole = 'start' | 'continuation' | 'end';

  /**
   * Defines how a step participates in a chainable block for drag-and-drop grouping.
   * Steps with the same blockType and adjacent positions are dragged as a group.
   */
  export interface BlockConnector {
    blockType: BlockType;
    role: BlockRole;
  }

  export interface StepKindInfo {
    name: string;
    description: string;
    // has child steps
    isDecorator?: boolean;
    // steps based on integration. Integration steps only show when integration enabled
    deviceKinds?: readonly DeviceKind[];
    // list of step kinds that may be children of this step. Also used in decorator steps when decoratorStepKind is not set.
    permittedChildStepKinds?: readonly StepKind[];
    // If true, the placeholder step (Add step below the decorator step) will always be shown. Otherwise, will only show if no child steps.
    alwaysShowDecoratorStepPlaceholder?: boolean;
    // For some steps (e.g. MoveArmToV2), want to only show 1 step in the decorator so that can add step with 1 click. If decoratorStepKind is not set, then will use permittedChildStepKinds when showing decorator. If permittedStepKinds is not set, will use all steps.
    decoratorStepKind?: StepKind;
    librarySection: LibrarySection;
    librarySort?: string;
    argumentKind: string;
    validator?: Step.Validator;
    toRoutineRunner: Step.ToRoutineRunner;
    getStepDescription?: Step.GetStepDescription;
    featureFlag?: FeatureFlagKey;
    // For steps that chain together (e.g., If/ElseIf/Else). Steps with the same blockType and adjacent positions are dragged as a group.
    blockConnector?: BlockConnector;
  }

  export function getPermittedChildStepKinds(
    stepKindInfo: StepKindInfo,
    featureFlags: Partial<Record<FeatureFlagKey, boolean>> = {},
  ): readonly StepKind[] | undefined {
    // Duck typing: if the stepKindInfo has its own getPermittedChildStepKinds function, use it
    if (
      'getPermittedChildStepKinds' in stepKindInfo &&
      typeof stepKindInfo.getPermittedChildStepKinds === 'function'
    ) {
      return stepKindInfo.getPermittedChildStepKinds(featureFlags);
    }

    // For other steps, return the standard permitted child steps
    return stepKindInfo.decoratorStepKind != null
      ? [stepKindInfo.decoratorStepKind]
      : stepKindInfo.permittedChildStepKinds;
  }
}
